import { Fragment, useEffect, useState } from 'react'
import { Box, Dialog, DialogTitle, DialogContent, IconButton, Typography, Stack, Divider, Chip } from "@mui/material"
import CloseIcon from '@mui/icons-material/Close'
import VisibilityIcon from '@mui/icons-material/Visibility'

const parseEplLines = (lines) => {
  const elements = []
  let labelWidth = 831
  let labelHeight = 200

  lines.forEach((line) => {
    const trimmed = line.trim()
    if (!trimmed) return

    const qMatch = trimmed.match(/^q(\d+)/)
    if (qMatch) {
      labelWidth = parseInt(qMatch[1], 10)
      return
    }

    const qMatchFull = trimmed.match(/^Q(\d+),/)
    if (qMatchFull) {
      labelHeight = parseInt(qMatchFull[1], 10)
      return
    }

    const aMatch = trimmed.match(/^A(\d+),(\d+),(\d),(\d),(\d),(\d),([NB]),"(.+)"$/)
    if (aMatch) {
      elements.push({
        type: 'text',
        x: parseInt(aMatch[1], 10),
        y: parseInt(aMatch[2], 10),
        rotation: parseInt(aMatch[3], 10),
        font: parseInt(aMatch[4], 10),
        hMult: parseInt(aMatch[5], 10),
        vMult: parseInt(aMatch[6], 10),
        text: aMatch[8],
      })
      return
    }

    const bMatch = trimmed.match(/^B(\d+),(\d+),(\d),(\d),(\d),(\d),(\d+),([BN]),"(.+)"$/)
    if (bMatch) {
      elements.push({
        type: 'barcode',
        x: parseInt(bMatch[1], 10),
        y: parseInt(bMatch[2], 10),
        rotation: parseInt(bMatch[3], 10),
        narrow: parseInt(bMatch[5], 10),
        wide: parseInt(bMatch[6], 10),
        height: parseInt(bMatch[7], 10),
        data: bMatch[9],
      })
      return
    }

    const pMatch = trimmed.match(/^P(\d+)$/)
    if (pMatch) {
      elements.push({
        type: 'quantity',
        quantity: parseInt(pMatch[1], 10),
      })
      return
    }
  })

  return { elements, labelWidth, labelHeight }
}

const SCALE = 0.55
const PADDING = 20

export default function ModalVistaPreviaEtiqueta({ open, onClose, lineaEtiqueta }) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [elements, setElements] = useState([])
  const [labelWidth, setLabelWidth] = useState(831)
  const [labelHeight, setLabelHeight] = useState(200)
  const [eplRaw, setEplRaw] = useState("")
  const [tab, setTab] = useState("preview")

  useEffect(() => {
    if (!open || !lineaEtiqueta) return

    const fetchPreview = async () => {
      setLoading(true)
      setError("")
      try {
        const formData = new FormData()
        formData.append("descripcion_articulo", lineaEtiqueta.descripcionArticulo || "")
        formData.append("medida", lineaEtiqueta.medida || "0")
        formData.append("codigo_barras", lineaEtiqueta.codigoBarras || "")
        formData.append("total_etiquetas", String(lineaEtiqueta.totalEtiquetas || 1))

        const response = await fetch("http://localhost:4000/api/vista-previa-etiqueta", {
          method: "POST",
          body: formData,
        })

        if (!response.ok) {
          throw new Error(`Error al obtener vista previa: ${response.statusText}`)
        }

        const data = await response.json()
        if (!data.success) {
          throw new Error(data.error || "Error al generar vista previa")
        }

        setEplRaw(data.epl || "")
        const parsed = parseEplLines(data.lines || [])
        setElements(parsed.elements)
        setLabelWidth(parsed.labelWidth)
        setLabelHeight(parsed.labelHeight)
      } catch (err) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    fetchPreview()
  }, [open, lineaEtiqueta])

  const previewWidth = Math.round(labelWidth * SCALE)
  const previewHeight = Math.round(labelHeight * SCALE)

  const toX = (x) => PADDING + Math.round(x * SCALE)
  const toY = (y) => PADDING + Math.round(y * SCALE)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <VisibilityIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Vista previa de etiqueta
          </Typography>
        </Stack>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {lineaEtiqueta && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label={`Artículo: ${lineaEtiqueta.clavearticulo || ""}`} size="small" variant="outlined" />
            <Chip label={`Medida: ${lineaEtiqueta.medida || ""}`} size="small" variant="outlined" />
            <Chip label={`Cant: ${lineaEtiqueta.totalEtiquetas || 0}`} size="small" variant="outlined" />
          </Stack>
        )}

        <Stack direction="row" spacing={1}>
          <Chip
            label="Vista previa"
            color={tab === "preview" ? "primary" : "default"}
            onClick={() => setTab("preview")}
            clickable
            size="small"
          />
          <Chip
            label="Código EPL"
            color={tab === "epl" ? "primary" : "default"}
            onClick={() => setTab("epl")}
            clickable
            size="small"
          />
        </Stack>

        {tab === "preview" && (
          <>
            {loading && (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                Generando vista previa...
              </Box>
            )}
            {error && (
              <Box sx={{ textAlign: 'center', py: 4, color: 'error.main' }}>
                {error}
              </Box>
            )}
            {!loading && !error && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  bgcolor: '#f3f4f6',
                  borderRadius: '8px',
                  p: 2,
                  minHeight: previewHeight + PADDING * 2 + 20,
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: previewWidth,
                    height: previewHeight,
                    bgcolor: '#ffffff',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                    border: '1px solid #d0d0d0',
                    borderRadius: '2px',
                    overflow: 'hidden',
                  }}
                >
                  {elements.map((el, idx) => {
                    if (el.type === 'text') {
                      const fontSize = Math.max(7, 8 + (el.vMult - 1) * 3)
                      return (
                        <Box
                          key={idx}
                          sx={{
                            position: 'absolute',
                            left: toX(el.x),
                            top: toY(el.y),
                            fontSize,
                            fontFamily: "'Courier New', monospace",
                            fontWeight: 500,
                            color: '#111',
                            whiteSpace: 'pre',
                            transform: el.rotation === 1 || el.rotation === 3 ? 'rotate(-90deg)' : 'none',
                            transformOrigin: 'left top',
                            lineHeight: 1,
                          }}
                        >
                          {el.text}
                        </Box>
                      )
                    }
                    if (el.type === 'barcode') {
                      const bcHeight = Math.round(el.height * SCALE)
                      const bcWidth = Math.round(120 * SCALE)
                      return (
                        <Box
                          key={idx}
                          sx={{
                            position: 'absolute',
                            left: toX(el.x),
                            top: toY(el.y),
                            width: bcWidth,
                            height: bcHeight,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <svg width={bcWidth} height={bcHeight} style={{ display: 'block' }}>
                            <rect x="0" y="0" width={bcWidth} height={bcHeight} fill="#fff" />
                            {Array.from({ length: 40 }, (_, i) => (
                              <rect
                                key={i}
                                x={Math.round((i / 40) * bcWidth)}
                                y="0"
                                width={Math.max(1, i % 2 === 0 ? 2 : 1)}
                                height={bcHeight}
                                fill={i % 2 === 0 ? '#111' : '#fff'}
                              />
                            ))}
                          </svg>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: 6,
                              fontFamily: "'Courier New', monospace",
                              color: '#111',
                              mt: 0.3,
                              lineHeight: 1,
                            }}
                          >
                            {el.data}
                          </Typography>
                        </Box>
                      )
                    }
                    if (el.type === 'quantity') {
                      return (
                        <Box
                          key={idx}
                          sx={{
                            position: 'absolute',
                            bottom: 2,
                            right: 4,
                            fontSize: 7,
                            fontFamily: "'Courier New', monospace",
                            color: '#666',
                          }}
                        >
                          x{el.quantity}
                        </Box>
                      )
                    }
                    return null
                  })}
                </Box>
              </Box>
            )}
          </>
        )}

        {tab === "epl" && (
          <Box
            sx={{
              bgcolor: '#1e1e1e',
              color: '#d4d4d4',
              fontFamily: "'Courier New', monospace",
              fontSize: '12px',
              p: 2,
              borderRadius: '6px',
              overflow: 'auto',
              maxHeight: '50vh',
              whiteSpace: 'pre',
              lineHeight: 1.4,
            }}
          >
            {loading ? "Generando..." : error ? error : eplRaw || "Sin datos EPL"}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}
